import {
  Badge,
  Box,
  Button,
  CloseButton,
  Divider,
  Flex,
  Grid,
  Group,
  Input,
  rem,
  Select,
  Text,
} from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import React, { forwardRef, useEffect, useRef, useState } from "react";
import { DropDownArrowSvg } from "../Svgs/DropDownArrowSvg";
import useStyles from "./style";
import BlueInfoSvg from "../Svgs/BlueInfoSvg";
import { RxCross1 } from "react-icons/rx";
import ClientTable from "./ClientTable";
import ActionButtons from "./ActionButtons";
import CustomBillingPagination from "../Pagination/CustomBillingPagination";
import useDebounce from "../../utils/useDebounce";
import { IconSearch } from "@tabler/icons-react";
import {
  getFacilityClients,
  getStaffById,
  updateAssignedStaffPatients,
} from "../../services/staff";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { getAllFacilities } from "../../services";

/**
 * StaffSettings component
 *
 * This component manages the settings related to staff and their assigned
 * facilities and clients. It allows selecting and managing facilities and
 * clients associated with a staff member. The component supports adding,
 * editing, and viewing modes of operation.
 *
 * @param {object} data - The staff data object containing staff details.
 * @param {string} mode - The mode of the component, can be "add", "edit", or "view".
 * @param {function} handleCancel - A function to handle the cancel action.
 * @param {object} staffResponse - The response object containing staff details after an action.
 * @param {string} activeTab - The currently active tab in the UI.
 */

export const StaffSettings = ({
  data,
  mode,
  handleCancel,
  staffResponse,
  activeTab,
  handleSave,
  form,
  addedFacilities,
  setAddedFacilities,
  selectedClientRows,
  setSelectedClientRows,
  loading
}) => {
  const { classes } = useStyles();
  const isMobile = useMediaQuery("(max-width: 600px)");
  const { facilityClients } = useSelector((state) => state.staff);
  const { facilityClientsCount } = useSelector((state) => state.staff);

  useEffect(() => {
    getAllFacilities();
  }, []);

  const icon = (
    <DropDownArrowSvg
      color={true}
      style={{ width: rem(0.5), height: rem(0.5) }}
    />
  );

  const [currentPage, setCurrentPage] = useState(1);
  const [showInfoBox, setShowInfoBox] = useState(true);
  const { id } = useParams(); // mode can be "view" or "edit"
  const { facilities } = useSelector((state) => state.facility);
  const selectRef = useRef(null);
  console.log("🚀 ~ StaffSettings ~ facilityClients:", facilityClients, id);

  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearch = useDebounce(searchTerm, 2000);

  // for view and  clients
  useEffect(() => {
    const facilityIds = addedFacilities?.map((f) => f?.value);
    getFacilityClients(facilityIds, data?.id, currentPage, debouncedSearch);
  }, [addedFacilities, debouncedSearch, currentPage]);

  useEffect(() => {
    if (mode === "edit" || mode === "view") {
      getStaffById(staffResponse?.id || data?.id);
    }
  }, []);

  useEffect(() => {
    if ((mode === "edit" || mode === "view") && facilityClients?.length) {
      setSelectedClientRows(
        facilityClients?.map((client) => {
          let isAssigntoStaff = client?.staff.find((i) => i.staffId === id);
          if (isAssigntoStaff?.isAssign) {
            isAssigntoStaff = true;
          } else {
            isAssigntoStaff = false;
          }
          let data = {
            clientId: client.id,
            isAssign: isAssigntoStaff,
          };
          return data;
        })
      );
    }
  }, [facilityClients, mode]);

  const FacilityItem = forwardRef(
    ({ label, value, onAddClick, added }, ref) => (
      <div ref={ref}>
        <Group
          position="apart"
          noWrap
          onClick={(e) => {
            // e.preventDefault(); // Prevent select from closing
            // e.stopPropagation();
            onAddClick({ label, value });
          }}
          style={{ cursor: "pointer", marginBottom: 5, marginInline: 10 }}
        >
          <Text>{label}</Text>
          <Button
            size="xs"
            disabled={added}
            variant="transparent"
            color="green"
            c={"green"}
          >
            {added ? "Added" : "Add"}
          </Button>
        </Group>
      </div>
    )
  );
  const handleRemoveFacility = (value) => {
    setAddedFacilities((prev) => prev?.filter((f) => f?.value !== value));
  };

  const handleAddFacility = (facility) => {
    if (!addedFacilities?.some((f) => f?.value === facility?.value)) {
      setAddedFacilities((prev) => [...prev, facility]);
      if (selectRef.current) {
        selectRef.current.blur();
      }
    }
  };

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  useEffect(() => {
    if (facilityClients?.length > 0) {
      const mappedClients = facilityClients?.map((client) => {
        let isAssign = false;
        if (mode === "add") {
          // In Add mode, select all by default
          isAssign = true;
        } else if (mode === "edit" || mode === "view") {
          const assignStatus = client?.staff?.find((i) => i.staffId === id);
          isAssign = assignStatus?.isAssign || false;
        }
        return {
          clientId: client.id,
          isAssign: isAssign,
        };
      });
      setSelectedClientRows(mappedClients);
    }
  }, [facilityClients, mode]);

  return (
    <Box mt={20}>
      <Grid>
        <Grid.Col span={isMobile ? 12 : 12}>
          {(mode === "add" || mode === "edit") && (
            <>
              <Text className={classes.clientTitle} mt={20}>
                Select Facility
              </Text>

              <Select
                ref={selectRef}
                placeholder="Select a facility"
                itemComponent={(props) => (
                  <FacilityItem
                    {...props}
                    onAddClick={handleAddFacility}
                    added={addedFacilities?.some(
                      (f) => f?.value === props?.value
                    )}
                  />
                )}
                data={
                  facilities?.facilities
                    ?.filter((facility) => facility?.active)
                    ?.map((facility) => ({
                      value: facility?.id,
                      label: facility?.name,
                    })) || []
                }
                searchable
                nothingFound="No facilities found"
                maxDropdownHeight={200}
                withinPortal
                rightSectionWidth={0}
                {...form.getInputProps("facilityId")}
                value={null}
                onChange={() => {}}
                rightSectionPointerEvents="none"
                rightSection={icon}
                disabled={mode === "view"}
              />
            </>
          )}

          <Group mt={10} gap="xs" wrap="wrap">
            {mode === "view" && (
              <>
                <Box mt={10}>
                  <Text mb={5}>Facility</Text>
                  <Group gap="xs" wrap="wrap" style={{ marginTop: ".5rem" }}>
                    {data?.facilityId?.map((f) => (
                      <Badge
                        key={f.id}
                        variant="light"
                        color="#F2F4F7"
                        radius="lg"
                        size="lg"
                        c={"#344054"}
                        style={{ background: "#F2F4F7" }}
                      >
                        {f?.name}
                      </Badge>
                    ))}
                  </Group>
                </Box>
              </>
            )}
            {(mode === "edit" || mode === "add") && (
              <>
                {addedFacilities?.map((f) => (
                  <Badge
                    key={f?.value}
                    variant="light"
                    color="gray"
                    radius="lg"
                    size="lg"
                    c={"#344054"}
                    rightSection={
                      <CloseButton
                        size="xs"
                        c="#344054"
                        onClick={() => handleRemoveFacility(f?.value)}
                      />
                    }
                  >
                    {f?.label}
                  </Badge>
                ))}
              </>
            )}
          </Group>
        </Grid.Col>
      </Grid>
      {showInfoBox && (
        <Box className={classes.infoBox}>
          <Flex align={"center"} gap={5} style={{ flex: 1 }}>
            <BlueInfoSvg />
            <Text>
              All Clients are selected by default. You can select/unselect
              individual clients from the list below.
            </Text>
          </Flex>
          <Box
            style={{ cursor: "pointer" }}
            onClick={() => setShowInfoBox(false)}
          >
            <RxCross1 />
          </Box>
        </Box>
      )}
      <Text className={classes.clientTitle} mt={20}>
        Select Client
      </Text>
      <Input
        icon={<IconSearch />}
        radius={"md"}
        size="md"
        placeholder="Search Client Name,Facility"
        className={classes.searchInput}
        onChange={(e) => setSearchTerm(e.target.value)}
      />
      <ClientTable
        selectedClientRows={selectedClientRows}
        setSelectedClientRows={setSelectedClientRows}
        data={data}
      />{" "}
      <CustomBillingPagination
        data={facilityClientsCount}
        itemsPerPage={10}
        currentPage={currentPage}
        handlePageChange={handlePageChange}
      />
      {(mode === "add" || mode === "edit") && (
        <>
          <Divider my={10} />
          <Box pt={20}>
            <ActionButtons
              onCancel={handleCancel}
              type="submit"
              activeTab={activeTab}
              onSave={handleSave}
              loading={loading}
            />
          </Box>
        </>
      )}
    </Box>
  );
};
